// The Home dashboard's rules, here rather than in HomePage.jsx, StatTiles,
// AgendaRow and data/useOnboarding.jsx, so the phones can draw the same
// dashboard from the same answers.
//
// Home is not the phones' "Today at a glance": that card is the calendar's Day
// view (`src/components/calendar/day/DayGlance.jsx`), four numbers about one
// day. Home is its own screen — the greeting, the getting-started checklist,
// four studio-wide figures and today's agenda — and this is its arithmetic
// and its words. The milestones themselves are `./onboarding.js`.
//
// ── The rules are `core/src/home.rs`'s ──────────────────────────────────────
//
// These are adapters: the greeting, the agenda's rows, the month's
// payments, the tiles, a row's time and status, the checklist's documents and
// when it shows. `HOME_WORDING` stays as the literal the screen reads, pinned
// to the core's by test/core/home.wasm.test.js.

import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { tombstoneOf } from './tombstone.js';

const str = (v) => (v === null || v === undefined ? null : String(v));

// "Let's get started, Ada!" — the first word of the teacher's display name,
// or of their email's local part, or "there".
export const greetingName = (displayName, email) => coreSync().greetingName(str(displayName), str(email));

export const greeting = (firstName) => coreSync().homeGreeting(String(firstName));

// Today's agenda: one row per student on each lesson today, and one with no
// student for a lesson nobody is on. `statusFor` is asked for every student.
export function agendaRows(occurrences = [], statusFor = () => 'unrecorded') {
  const lessons = occurrences.map((o) => String(o.id));
  const students = [];
  const attendance = [];
  occurrences.forEach((o, i) => {
    for (const sid of o.studentIds || []) {
      students.push({ lesson: i, studentId: String(sid) });
      const status = statusFor(o.id, sid);
      if (status) attendance.push({ occurrenceId: String(o.id), studentId: String(sid), status: String(status) });
    }
  });
  return coreSync().agendaRows(lessons, students, attendance).map((r) => ({
    key: r.key,
    occurrenceId: occurrences[r.lesson].id,
    studentId: r.studentId ?? null,
    status: r.status,
  }));
}

// What a studio has been paid this month: live payments dated in the month
// `today` is in. `today` is the date-only string the agenda is drawn for —
// `todayISO()`, the teacher's own calendar day — and the core takes the month
// from it, not from the UTC clock, which on the last evening of a month in
// the Americas is already next month. A bare `YYYY-MM` is read as that month.
export function paymentsInMonth(entries = [], today) {
  return coreSync().paymentsInMonth(entries.map((e) => ({
    kind: String(e?.kind ?? ''),
    amountMinor: Number(e?.amountMinor) || 0,
    occurredOn: str(e?.occurredOn),
    ...tombstoneOf(e),
  })), String(today));
}

// The four tiles, in order. `owedMinor` and `paymentsMinor` are money; the
// owed tile says how many ledger lines could not be read, when any.
export function homeTiles({ lessonsToday, activeStudents, owedMinor, unreadable = 0, paymentsMinor }) {
  return coreSync().homeTiles(
    Number(lessonsToday) || 0, Number(activeStudents) || 0, Number(owedMinor) || 0,
    Number(unreadable) || 0, Number(paymentsMinor) || 0,
  ).map((t) => (t.money ? { label: t.label, value: t.value, money: true } : { label: t.label, value: t.value }));
}

// An agenda row's time: "16:00 – 30 min".
export const agendaTime = (startTime, durationMin) => coreSync().agendaTime(String(startTime), Number(durationMin));

// An agenda row's status: its label, and " ($)" when the lesson bills (#362).
export const agendaStatus = (status, requiresMakeupCredit) => (
  coreSync().agendaStatus(String(status), requiresMakeupCredit === true)
);

// The screen's words.
export const HOME_WORDING = Object.freeze({
  title: 'Home',
  agenda: 'Agenda',
  emptyTitle: 'No Events Scheduled For Today',
  emptyDescription: 'Check the calendar to view or add events.',
  emptyAction: 'Go To Calendar',
  editAttendance: 'Edit Attendance',
  dismissChecklist: 'Dismiss getting started',
  checklistLabel: 'Getting started',
});

// The getting-started checklist's document, `settings/onboarding`: written
// once at signup, and merged with the dismissal.
export const onboardingCreateDocument = (serverTimestamp) => (
  documentFromRows(coreSync().onboardingCreateDocument(), serverTimestamp)
);
export const onboardingDismissDocument = (serverTimestamp) => (
  documentFromRows(coreSync().onboardingDismissDocument(), serverTimestamp)
);

// Whether the checklist shows: not dismissed, and not every milestone done.
export const checklistVisible = (dismissed, allDone) => coreSync().checklistVisible(Boolean(dismissed), Boolean(allDone));

// ── Nothing said before it is read (#629) ──────────────────────────────────
//
// Which reads each tile is folded from, in `homeTiles`' order: lessons today
// from the schedule, Active Students from the roster, and both money tiles
// from the ledger. A tile whose reads have not answered shows the dash
// (`figureOrDash`, src/domain/reading.js) rather than a zero that reads as
// "nothing booked" or "nobody owes you" during a cold start.
// You're owed is folded over the families as well as the ledger (#640): before
// the families answer it is the sum over none of them, a zero that is a claim.
export const homeTilesKnown = ({ series, students, ledger, families = true }) => [
  Boolean(series), Boolean(students), Boolean(ledger) && Boolean(families), Boolean(ledger),
];

// The checklist is a claim about the studio — *Schedule an Event* unticked
// says it has never scheduled one — so it waits for every read its milestones
// are counted from, and for `settings/onboarding`, without which a dismissed
// checklist flashes back in. Until then it is not drawn at all (#629).
export const checklistShows = ({ read, dismissed, allDone }) => Boolean(read) && checklistVisible(dismissed, allDone);
